import { useSeo } from '../lib/seo';
/**
 * Event detail page — destination when an event card is clicked without an external URL.
 * Also acts as a graceful fallback if an external link is broken.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { buildEventSchema } from '../lib/eventSchema';
import { ArrowLeft, ArrowUpRight, Calendar, Globe, MapPin, PoundSterling, Ticket } from 'lucide-react';
import api from '../lib/api';
import { mapsUrl } from '../components/ClickableAddress';
import SmartImage from '../components/SmartImage';
import AttractionCard from '../components/AttractionCard';
import { eventPlaceholder } from '../lib/eventPlaceholder';

export default function EventDetail() {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  useSeo(
    e ? `${e.title} — What's On in ${e.town || 'Leicestershire'} | GoLeicestershire` : null,
    e?.summary || e?.description?.slice(0, 160)
  );
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setE(null); setNotFound(false);
    api.get(`/events/${slug}`)
      .then((r) => setE(r.data))
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
        <Helmet><title>Event not found · GoLeicestershire</title><meta name="robots" content="noindex" /></Helmet>
        <h1 className="font-display text-4xl mb-3">Event not found</h1>
        <p className="text-muted mb-6">This event may have ended or been removed.</p>
        <Link to="/events" className="bg-heritage text-base px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2">
          See all events <ArrowUpRight size={14}/>
        </Link>
      </div>
    );
  }
  if (!e) return <div className="py-32 text-center text-muted">Loading…</div>;

  const startStr = new Date(e.start_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const endStr = e.end_date && new Date(e.end_date).toDateString() !== new Date(e.start_date).toDateString()
    ? new Date(e.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
    : null;

  return (
    <div data-testid={`event-detail-${slug}`}>
      <Helmet>
        <title>{`${e.title} — What's On in ${e.town || 'Leicestershire'} | GoLeicestershire`}</title>
        <meta name="description" content={e.summary || `${e.title} in ${e.town || 'Leicestershire'} — dates, venue and details on GoLeicestershire's What's On calendar.`} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          ...buildEventSchema({ ...e, slug }),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://goleicestershire.com/' },
            { '@type': 'ListItem', position: 2, name: "What's On", item: 'https://goleicestershire.com/events' },
            { '@type': 'ListItem', position: 3, name: e.title, item: `https://goleicestershire.com/events/${slug}` },
          ],
        })}</script>
      </Helmet>

      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 pt-8">
        <Link to="/events" className="inline-flex items-center gap-2 text-muted hover:text-heritage text-sm" data-testid="event-back">
          <ArrowLeft size={16}/> All events
        </Link>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-8 md:py-12">
        <div className="relative aspect-[16/9] rounded-3xl overflow-hidden grain mb-8 md:mb-12">
          <div className="absolute inset-0">
            <SmartImage src={e.hero_image || eventPlaceholder(e.category)}
              fallback={eventPlaceholder(e.category)}
              alt={e.title}/>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent pointer-events-none"/>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
          <div className="lg:col-span-2">
            <div className="label text-heritage mb-3 inline-flex items-center gap-2"><Calendar size={12}/> {e.category || 'Event'}</div>
            {e.nominee_badge && (!e.nominee_badge_until || new Date(e.nominee_badge_until + 'T23:59:59') >= new Date()) && (
              <span data-testid="event-detail-nominee-badge"
                className="inline-flex items-center gap-1.5 bg-forest text-[#FAF8F5] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                🏆 {e.nominee_badge}
              </span>
            )}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">{e.title}</h1>
            {e.summary && <p className="text-xl md:text-2xl text-charcoal/75 font-display leading-snug mb-8">{e.summary}</p>}
            {e.description && (
              <div className="prose prose-lg max-w-none text-charcoal/85 whitespace-pre-wrap leading-relaxed" data-testid="event-description">
                {e.description}
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-white border border-charcoal/10 rounded-3xl p-6 md:p-8 lg:sticky lg:top-28">
              <div className="space-y-5">
                <div>
                  <div className="label text-charcoal/50 mb-1.5">When</div>
                  <div className="font-display text-lg leading-tight">
                    {startStr}{endStr && <> – {endStr}</>}
                  </div>
                </div>
                {(e.venue || e.town || e.address) && (
                  <div>
                    <div className="label text-charcoal/50 mb-1.5">Where</div>
                    <div className="font-display text-lg leading-tight">{e.venue || e.town}</div>
                    {e.address && (
                      <a href={mapsUrl(e.address)} target="_blank" rel="noreferrer"
                        data-testid="event-address-link"
                        className="text-muted text-sm mt-1 inline-flex items-start gap-1.5 hover:text-heritage underline-offset-2 hover:underline"
                        title="Open in Google Maps">
                        <MapPin size={13} className="mt-0.5 shrink-0"/>
                        <span>{e.address}</span>
                      </a>
                    )}
                  </div>
                )}
                {e.price && (
                  <div>
                    <div className="label text-charcoal/50 mb-1.5">Price</div>
                    <div className="font-display text-lg inline-flex items-center gap-2"><PoundSterling size={16} className="text-muted"/> {e.price}</div>
                  </div>
                )}
              </div>
              <div className="mt-7 space-y-2">
                {e.url && (
                  <a href={e.url} target="_blank" rel="noreferrer" data-testid="event-cta-tickets" data-outbound="book"
                    className="bg-heritage text-base w-full px-5 py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 hover:bg-heritage-deep transition-colors">
                    <Ticket size={16}/> More info &amp; tickets <ArrowUpRight size={14}/>
                  </a>
                )}
                {e.website && e.website !== e.url && (
                  <a href={e.website} target="_blank" rel="noreferrer" data-testid="event-cta-website"
                    className="bg-admin border border-charcoal/15 hover:border-heritage text-charcoal w-full px-5 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                    <Globe size={15}/> Visit venue website <ArrowUpRight size={13}/>
                  </a>
                )}
                {e.address && (
                  <a href={mapsUrl(e.address)} target="_blank" rel="noreferrer" data-testid="event-cta-directions"
                    className="bg-admin border border-charcoal/15 hover:border-heritage text-charcoal w-full px-5 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                    <MapPin size={15}/> Directions <ArrowUpRight size={13}/>
                  </a>
                )}
                {!e.url && !e.website && (
                  <div className="text-muted text-sm py-2" data-testid="event-no-link-notice">No direct booking link yet — check with the venue.</div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Onward discovery — keep the visit going */}
      {(e.nearby_attractions?.length > 0 || e.nearby_food?.length > 0 || e.other_events?.length > 0) && (
        <div className="bg-stone/40 py-14 mt-4" data-testid="event-nearby-section">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 space-y-12">
            {e.nearby_attractions?.length > 0 && (
              <div data-testid="event-nearby-attractions">
                <h2 className="font-display text-3xl mb-6">Make a day of it {e.town ? `in ${e.town}` : 'nearby'}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {e.nearby_attractions.map((a) => <AttractionCard key={a.slug} a={a} />)}
                </div>
              </div>
            )}
            {e.nearby_food?.length > 0 && (
              <div data-testid="event-nearby-food">
                <h2 className="font-display text-3xl mb-6">Places to eat &amp; drink {e.town ? `in ${e.town}` : 'nearby'}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {e.nearby_food.map((a) => <AttractionCard key={a.slug} a={a} />)}
                </div>
              </div>
            )}
            {e.other_events?.length > 0 && (
              <div data-testid="event-other-events">
                <h2 className="font-display text-3xl mb-6">More events {e.town ? `in ${e.town}` : 'you might like'}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {e.other_events.map((ev) => (
                    <Link key={ev.slug || ev.id} to={ev.slug ? `/events/${ev.slug}` : '/events'}
                      data-testid={`other-event-${ev.slug}`}
                      className="group bg-white border border-charcoal/10 rounded-2xl p-5 flex items-start gap-4 hover:shadow-soft hover:-translate-y-0.5 transition-all">
                      <Calendar size={18} className="text-heritage mt-1 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs text-muted mb-1">
                          {new Date(ev.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          {ev.venue ? ` · ${ev.venue}` : ev.town ? ` · ${ev.town}` : ''}
                        </div>
                        <div className="font-display text-lg leading-tight truncate group-hover:text-heritage transition-colors">{ev.title}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
